import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import {
    ContainerComponent,
    IconComponent,
    IDynamicElement,
    ImageData,
    ImageDimensions,
    ImageItemComponent,
    LinkDirective,
} from '@sirius/common';
import { ICustomerSuccessCard, ICustomerSuccessPanel } from 'shared/models';

let nextPanelId = 0;

// `sirius-image-item` reads five ImageDimensions: [0] serves below `lg` and doubles as the plain `src`, [1] serves
// lg–xl, [2] xl–xxl, [3] xxl and up, [4] is legacy and unread. All 16:9. Widths are the boxes' rendered widths:
// below `lg` the grid is one column so both boxes span the panel; from `lg` the featured photo is ~590px wide and
// a row photo ~215px. Retina sources are generated from these automatically.
const FEATURED_PHOTO = new ImageData('customer-success-featured', [
    new ImageDimensions(896, 504),
    new ImageDimensions(592, 333),
    new ImageDimensions(592, 333),
    new ImageDimensions(592, 333),
    new ImageDimensions(592, 333),
]);

const ROW_PHOTO = new ImageData('customer-success-row', [
    new ImageDimensions(688, 387),
    new ImageDimensions(224, 126),
    new ImageDimensions(224, 126),
    new ImageDimensions(224, 126),
    new ImageDimensions(224, 126),
]);

/** The panel shows at most one featured card and three rows. The backend caps the list too; this is belt and braces. */
const MAX_CARDS = 4;

/**
 * The Cobot Customer Success panel: one featured case story and up to three secondary rows on a dark band.
 * Registered as the `customer-success-panel` dynamic element (CMS block `customer-success-panel-v1`). Every card
 * arrives fully resolved from the backend; this component only lays it out.
 */
@Component({
    selector: 'ur-customer-success-panel',
    templateUrl: './customer-success-panel.component.html',
    styleUrl: './customer-success-panel.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ContainerComponent, IconComponent, ImageItemComponent, LinkDirective, NgTemplateOutlet],
})
export class CustomerSuccessPanelComponent implements IDynamicElement<ICustomerSuccessPanel> {
    readonly config = input<ICustomerSuccessPanel>();

    protected readonly headingId = `ur-customer-success-title-${nextPanelId++}`;
    protected readonly featuredPhoto = FEATURED_PHOTO;
    protected readonly rowPhoto = ROW_PHOTO;

    protected readonly title = computed(() => this.config()?.title?.trim() || null);
    protected readonly ctaText = computed(() => this.config()?.ctaText ?? '');
    protected readonly notice = computed(() => this.config()?.textHtml || null);
    protected readonly cards = computed<ICustomerSuccessCard[]>(() => (this.config()?.cards ?? []).slice(0, MAX_CARDS));
    protected readonly featured = computed(() => this.cards()[0] ?? null);
    protected readonly secondary = computed(() => this.cards().slice(1));
    protected readonly hasContent = computed(() => this.cards().length > 0 || this.notice() !== null);
}
